import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { commerceWorkflowApi, type CommerceProcess, type SagaExecution, type WorkflowDashboard } from '../../api/commerceworkflow';

const PROCESSES = [
  'product-lifecycle', 'order-fulfilment', 'purchase', 'return', 'customer',
];

export function WorkflowCenterPage() {
  const [dashboard, setDashboard] = useState<WorkflowDashboard | null>(null);
  const [processes, setProcesses] = useState<CommerceProcess[]>([]);
  const [sagas, setSagas] = useState<SagaExecution[]>([]);
  const [selected, setSelected] = useState<string>('order-fulfilment');
  const [error, setError] = useState('');
  const [executing, setExecuting] = useState(false);

  useEffect(() => {
    void commerceWorkflowApi.dashboard()
      .then(setDashboard)
      .catch((e) => setError(String(e)));
    void commerceWorkflowApi.processRegistry()
      .then((r) => setProcesses(r.processes))
      .catch(() => undefined);
    void commerceWorkflowApi.listSagas()
      .then((r) => setSagas(r.sagas ?? []))
      .catch(() => undefined);
  }, []);

  const selectedProcess = processes.find((p) => p.id === selected);

  async function executeProcess() {
    setExecuting(true);
    setError('');
    try {
      const saga = await commerceWorkflowApi.executeProcess(selected);
      setSagas((prev) => [saga, ...prev]);
    } catch (e) {
      setError(String(e));
    } finally {
      setExecuting(false);
    }
  }

  return (
    <div className="mx-auto max-w-6xl p-6">
      <Link to="/workflows" className="text-sm text-blue-600 hover:underline dark:text-blue-400">← Orchestration Hub</Link>
      <Link to="/workflows/sagas" className="ml-4 text-sm text-blue-600 hover:underline dark:text-blue-400">Saga Dashboard →</Link>
      <h1 className="mt-2 text-2xl font-semibold">Workflow Center</h1>
      <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
        DEV-000056 — enterprise workflow orchestration · DEV-000059 — persistent saga engine
      </p>
      {error && <p className="mt-4 text-sm text-red-600 dark:text-red-400">{error}</p>}

      <div className="mt-6 grid gap-4 sm:grid-cols-4">
        <div className="rounded-xl border p-4 dark:border-slate-700">
          <p className="text-sm text-slate-500">Processes</p>
          <p className="text-2xl font-semibold">{dashboard?.processes ?? processes.length}</p>
        </div>
        <div className="rounded-xl border p-4 dark:border-slate-700">
          <p className="text-sm text-slate-500">Active sagas</p>
          <p className="text-2xl font-semibold">{dashboard?.active_sagas ?? sagas.length}</p>
        </div>
        <div className="rounded-xl border p-4 dark:border-slate-700">
          <p className="text-sm text-slate-500">Commerce modules</p>
          <p className="text-2xl font-semibold">{dashboard?.commerce_modules ?? 16}</p>
        </div>
        <div className="rounded-xl border p-4 dark:border-slate-700">
          <p className="text-sm text-slate-500">Running executions</p>
          <p className="text-2xl font-semibold">{dashboard?.dashboard?.running_executions ?? 0}</p>
        </div>
      </div>

      <div className="mt-8 flex flex-wrap gap-2">
        {PROCESSES.map((id) => (
          <button
            key={id}
            type="button"
            onClick={() => setSelected(id)}
            className={`rounded-lg px-3 py-1 text-sm ${selected === id ? 'bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900' : 'border dark:border-slate-700'}`}
          >
            {id}
          </button>
        ))}
      </div>

      {selectedProcess && (
        <section className="mt-8 rounded-xl border p-4 dark:border-slate-700">
          <h2 className="text-lg font-medium">{selectedProcess.name}</h2>
          <p className="text-sm text-slate-500">{selectedProcess.description}</p>
          <p className="mt-2 text-sm">Owner: {selectedProcess.owner} · SLA: {selectedProcess.sla}</p>
          <div className="mt-4 space-y-2">
            {selectedProcess.steps.map((step, i) => (
              <div key={step.id} className="flex items-center gap-3 text-sm">
                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-slate-100 text-xs dark:bg-slate-800">{i + 1}</span>
                <span className="font-medium">{step.name}</span>
                <span className="text-slate-500">→ {step.module_id}</span>
              </div>
            ))}
          </div>
          <button
            type="button"
            disabled={executing}
            onClick={() => void executeProcess()}
            className="mt-4 rounded-lg bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {executing ? 'Executing…' : 'Execute process'}
          </button>
        </section>
      )}

      <section className="mt-8">
        <h2 className="text-lg font-medium">Saga dashboard</h2>
        <div className="mt-3 space-y-2">
          {sagas.length === 0 && (
            <p className="text-sm text-slate-500">No saga executions yet. Execute a process to start orchestration.</p>
          )}
          {sagas.map((s) => (
            <div key={s.id} className="rounded-lg border p-3 dark:border-slate-700">
              <p className="font-medium">{s.process_id}</p>
              <p className="text-sm text-slate-500">{s.status} · {s.id.slice(0, 8)}…</p>
              <div className="mt-2 flex flex-wrap gap-1">
                {s.steps.map((step) => (
                  <span key={step.id} className="rounded bg-slate-100 px-2 py-0.5 text-xs dark:bg-slate-800">
                    {step.name}: {step.status}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
